import { z } from 'zod';

// Streaks, as in Lagos Life: two players keep a streak going by each doing one thing together every London day
// before midnight. What counts is set by the admins (things done together: something social, a meal bought for
// them, a visit, a voice note; never plain messages, stickers or money). Doing more the same day adds nothing.
// Miss a day and it ends, except that a missed day can be forgiven a set number of times a week ("this week's
// save is holding it").

export const STREAK_KINDS = {
  social: { label: 'Something together (say hello, gist, a joke…)', icon: '💬' },
  meal: { label: 'Buying them a meal', icon: '🍛' },
  visit: { label: 'A visit to a home', icon: '🏠' },
  voice: { label: 'A voice note', icon: '🎤' },
} as const;
export type StreakKind = keyof typeof STREAK_KINDS;
export const STREAK_KIND_KEYS = Object.keys(STREAK_KINDS) as StreakKind[];

export const StreakRules = z.object({
  counts: z
    .array(z.enum(STREAK_KIND_KEYS as [StreakKind, ...StreakKind[]]))
    .min(1)
    .max(STREAK_KIND_KEYS.length),
  // missed days forgiven each week (Monday to Sunday, London time)
  forgivenPerWeek: z.number().int().min(0).max(3),
});
export type StreakRules = z.infer<typeof StreakRules>;
export const DEFAULT_STREAK_RULES: StreakRules = { counts: [...STREAK_KIND_KEYS], forgivenPerWeek: 1 };

// ---- the arithmetic on London days ('YYYY-MM-DD')
const utc = (day: string) => {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year!, month! - 1, date!);
};
export const daysBetween = (from: string, to: string) => Math.round((utc(to) - utc(from)) / 86_400_000);
export const addDays = (day: string, days: number) => new Date(utc(day) + days * 86_400_000).toISOString().slice(0, 10);
// the Monday that starts the day's week
export const weekOf = (day: string) => {
  const weekday = (new Date(utc(day)).getUTCDay() + 6) % 7; // Monday 0
  return addDays(day, -weekday);
};

// what is kept for a pair of players
export type StreakState = {
  count: number;
  best: number;
  // the last day both did their part
  lastDay: string | null;
  // the week saves were last used in, and how many
  saveWeek: string | null;
  savesUsed: number;
};
export const NO_STREAK: StreakState = { count: 0, best: 0, lastDay: null, saveWeek: null, savesUsed: 0 };
const savesLeft = (state: StreakState, week: string, rules: StreakRules) =>
  rules.forgivenPerWeek - (state.saveWeek === week ? state.savesUsed : 0);

// both did their part on `day`: the streak grows (over one forgiven missed day if a save is left), or starts again
export function advanceStreak(state: StreakState, day: string, rules: StreakRules): StreakState {
  if (state.lastDay === day) return state;
  const gap = state.lastDay ? daysBetween(state.lastDay, day) : Infinity;
  const missed = addDays(day, -1);
  if (gap === 1) return grow(state, day);
  if (gap === 2 && state.count > 0 && savesLeft(state, weekOf(missed), rules) > 0) {
    const week = weekOf(missed);
    return {
      ...grow(state, day),
      saveWeek: week,
      savesUsed: (state.saveWeek === week ? state.savesUsed : 0) + 1,
    };
  }
  return { ...state, count: 1, best: Math.max(state.best, 1), lastDay: day };
}
const grow = (state: StreakState, day: string): StreakState => ({
  ...state,
  count: state.count + 1,
  best: Math.max(state.best, state.count + 1),
  lastDay: day,
});

// how a streak stands today: its length (0 once it has ended), and whether a save is covering yesterday
export function streakToday(state: StreakState, today: string, rules: StreakRules) {
  if (!state.lastDay || state.count === 0) return { count: 0, holding: false };
  const gap = daysBetween(state.lastDay, today);
  if (gap <= 1) return { count: state.count, holding: false };
  if (gap === 2 && savesLeft(state, weekOf(addDays(today, -1)), rules) > 0)
    return { count: state.count, holding: true };
  return { count: 0, holding: false };
}

// what a chat shows
export const StreakView = z.object({
  count: z.number().int().min(0),
  best: z.number().int().min(0),
  // each of you has done your part today
  mine: z.boolean(),
  theirs: z.boolean(),
  // a forgiven missed day is keeping it alive (do something today)
  holding: z.boolean(),
  // when today ends in London
  endsAt: z.string(),
});
export type StreakView = z.infer<typeof StreakView>;
